// Pure helper: given a URL hostname, the configured platform host,
// and the slug of the org the session's JWT belongs to, decide
// whether to render the page, redirect to the correct subdomain, or
// no-op (apex / marketing / kodelauget cross-eTLD).
//
// Why this exists: post-#825 apex-shared cookies, an operator whose
// JWT is for org A can navigate to tenant-B.stemne.no and the
// browser will send the access_token. The backend's AdminAuth
// workspace-match guard refuses admin data calls with a typed 403
// `wrong_workspace` there (only /api/admin/me and /me/auth-state
// stay reachable, so the SPA can learn auth_org_slug). This helper
// makes the SPA detect the mismatch proactively and navigate to the
// session's own subdomain before the page paints; apiClient's
// wrong_workspace handler is the reactive counterpart for data calls
// that fire before (or instead of) the router guard.

export interface MismatchCheck {
  // hostname from window.location (e.g. 'tenant-b.stemne.no',
  // 'kodelauget.no', 'localhost', 'localhost:5173')
  hostname: string
  // PLATFORM_HOST (e.g. 'stemne.no', 'qa.stemne.no', 'localhost').
  // Used to extract the slug label and to decide whether the
  // hostname is the apex or a subdomain we manage.
  platformHost: string
  // The slug of the org the JWT belongs to. Empty string = unknown
  // (no session, or backend couldn't resolve) → no mismatch action.
  authOrgSlug: string
}

export type MismatchAction =
  | { kind: 'ok' }
  // 'redirect' is the soft case: tell the SPA to navigate to the
  // correct subdomain. Used for read-only navigation; preserves
  // path + query so a /dashboard/customers link works.
  | { kind: 'redirect'; to: string }
  // 'block' is the hard case: the mismatch is severe enough that
  // we don't want to render the page or auto-redirect (e.g. the
  // operator might want to stay and explicitly switch tenants).
  // The SPA shows an interstitial.
  | { kind: 'block'; sessionSlug: string; urlSlug: string }

// extractUrlSlug pulls the first label of hostname when the host is
// a known subdomain under platformHost. Returns empty string when
// the host is the apex itself, a non-platform host (kodelauget.no),
// or anything else not under our control.
export function extractUrlSlug(hostname: string, platformHost: string): string {
  if (!hostname || !platformHost) return ''
  // Strip the port the browser includes in window.location.host
  // (platformHost may or may not include it — normalise both).
  const hostNoPort = hostname.replace(/:\d+$/, '')
  const platformNoPort = platformHost.replace(/:\d+$/, '')
  if (hostNoPort === platformNoPort) return ''
  if (!hostNoPort.endsWith(`.${platformNoPort}`)) return ''
  const head = hostNoPort.slice(0, hostNoPort.length - platformNoPort.length - 1)
  // Multi-label heads (e.g. 'foo.bar.stemne.no') should be treated
  // as 'foo' — the first label. Stemne never provisions multi-label
  // tenant slugs. This conservative choice means we don't false-
  // positive on www.foo.stemne.no edge cases either.
  const firstDot = head.indexOf('.')
  return firstDot === -1 ? head : head.slice(0, firstDot)
}

// checkTenantMismatch is the decision function. Pure (no Vue / no
// router), unit-testable in isolation.
export function checkTenantMismatch(input: MismatchCheck): MismatchAction {
  const { hostname, platformHost, authOrgSlug } = input

  // No session, or backend couldn't tell us — never block. The route's
  // own auth gate will redirect to /login if needed.
  if (!authOrgSlug) return { kind: 'ok' }

  const urlSlug = extractUrlSlug(hostname, platformHost)

  // Apex (no slug in URL): no comparison possible, allow. The apex
  // marketing surface is global; the operator just happens to be
  // signed in on the apex (e.g. via /staff or stale signup flow).
  if (!urlSlug) return { kind: 'ok' }

  // Slugs match: the page is for the operator's own tenant. Render.
  if (urlSlug === authOrgSlug) return { kind: 'ok' }

  // Mismatch: the operator is on a tenant URL that isn't theirs.
  // 'block' so the SPA can show an explicit "you're signed in as X
  // for tenant Y; switch?" interstitial rather than silently
  // redirecting (which would surprise an operator who genuinely
  // wanted to view someone else's public booking page).
  return { kind: 'block', sessionSlug: authOrgSlug, urlSlug }
}
